import {useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useLocation} from '@github-ui/react-router'

import {useRouteQueryForQuery} from '../hooks/use-route-query-for-query'
import {savedViewContentPath, type SavedViewRoutePayload, savedViewRouteSearchParams} from './saved-view-route-payload'
import {savedViewRoute} from './saved-views-routes'

interface SavedViewContentForQuery {
  route: SavedViewRoutePayload
  /** When `route` last changed. */
  dataUpdatedAt: number
  /** A response for `query` is loading, so `route` is a placeholder from another query. */
  isFetching: boolean
  /** The response for `query` failed, so `route` is from another query. */
  isError: boolean
}

/**
 * The route payload for `query`. The route answers for the URL query, or for the stored one when
 * the URL has none, so a query shown without navigating (an editor preview, or a query saved from
 * the editor) is fetched as the route would fetch the URL that states it.
 */
export function useSavedViewContentForQuery(query: string): SavedViewContentForQuery {
  const {data: route, dataUpdatedAt} = useSuspenseRouteQuery(savedViewRoute, 'mainQuery')
  const {pathname, search} = useLocation()
  const servedQuery = new URLSearchParams(search).get('q') ?? route.view?.savedView.query
  // Without a stated query to compare, the route response is the best answer.
  const isServed = servedQuery === undefined || servedQuery.trim() === query.trim()

  const forQuery = useRouteQueryForQuery<SavedViewRoutePayload>(savedViewRoute, 'mainQuery', {
    query,
    pathname: savedViewContentPath(pathname),
    readSearchParams: savedViewRouteSearchParams,
    // The list loads its results for `query` on its own.
    enabled: !isServed && route.layout === 'board',
  })

  if (isServed || route.layout !== 'board') return {route, dataUpdatedAt, isFetching: false, isError: false}

  return {
    route: forQuery.data ?? route,
    dataUpdatedAt: forQuery.data ? forQuery.dataUpdatedAt : dataUpdatedAt,
    isFetching: forQuery.isFetching,
    isError: forQuery.isError,
  }
}
